/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativos, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaRetail.module.scss";

// =====================================================
// DATOS IPSOS SUPERMERCADOS (Base: 400 casos / 338 usuarios)
// =====================================================
const IPSOS_SUPER = {
  penetracion: 0.851, // 85.1% hace compras en supermercados
  noPenetracion: 0.149,
  frecuenciaCompra: {
    diario: { pct: 0.026, factor: 30, ticket: 12500 }, // compras pequeñas diarias
    variasSemana: { pct: 0.199, factor: 12, ticket: 37500 }, // compras medianas
    semanal: { pct: 0.356, factor: 4, ticket: 54000 }, // compra semanal típica
    quincenal: { pct: 0.211, factor: 2, ticket: 187500 }, // compra grande quincenal
    mensual: { pct: 0.208, factor: 0.75, ticket: 375000 }, // compra mensual grande
  },
  marketShare: {
    Líder: 0.453,
    Jumbo: 0.278,
    Unimarc: 0.119,
    "Santa Isabel": 0.082,
    Tottus: 0.05,
    Otros: 0.018,
  },
  // Por qué prefieren ese supermercado (datos interesantes para disclaimer)
  motivosPreferencia: {
    cercania: 0.484,
    ofertas: 0.395,
    preciosBajos: 0.38,
    variedad: 0.267,
  },
};

// =====================================================
// DATOS IPSOS TIENDAS DE CONVENIENCIA (Base: 400 casos)
// =====================================================
const IPSOS_CONVENIENCIA = {
  penetracion: 0.857, // 85.7% usa tiendas de conveniencia
  noPenetracion: 0.143,
  frecuenciaUso: {
    diario: { pct: 0.13, factor: 30 }, // 13% las usa diariamente
    variasSemana: { pct: 0.437, factor: 12 }, // 43.7% varias veces por semana
    semanal: { pct: 0.256, factor: 4 }, // 25.6% una vez por semana
    quincenal: { pct: 0.095, factor: 2 }, // 9.5% quincenalmente
    mensual: { pct: 0.082, factor: 0.75 }, // resto mensual o menos
  },
  ticketPromedio: 8000, // CLP - compras pequeñas
  marketShare: {
    Oxxo: 0.337,
    Castaño: 0.222,
    "OK Market": 0.112,
    Pronto: 0.098,
    "Upa!": 0.087,
    Upita: 0.056,
    Spid: 0.038,
    Otros: 0.05,
  },
};

// Escenarios de captación
const ESCENARIOS_CAPTACION = [2, 4, 6, 8, 10];

export const Retail = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [supermercadoSeleccionado, setSupermercadoSeleccionado] = useState("Líder");
  const [convenienciaSeleccionada, setConvenienciaSeleccionada] = useState("Oxxo");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(4);
  const [vistaActiva, setVistaActiva] = useState("super"); // 'super' o 'conveniencia'
  const [verAnual, setVerAnual] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativos({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // =====================================================
  // CÁLCULOS SUPERMERCADOS
  // =====================================================
  const resultadosSuper = useMemo(() => {
    if (!totalEjecutivos) return null;

    const compranSuper = Math.round(totalEjecutivos * IPSOS_SUPER.penetracion);
    const noCompran = Math.round(totalEjecutivos * IPSOS_SUPER.noPenetracion);

    // Desglose por frecuencia
    const desgloseFrecuencia = {};
    let consumosMensuales = 0;
    let mercadoMensual = 0;

    Object.entries(IPSOS_SUPER.frecuenciaCompra).forEach(([freq, data]) => {
      const personas = Math.round(compranSuper * data.pct);
      const consumos = Math.round(personas * data.factor);
      const gasto = consumos * data.ticket;

      desgloseFrecuencia[freq] = {
        pct: data.pct,
        personas,
        consumos,
        ticket: data.ticket,
        gasto,
      };

      consumosMensuales += consumos;
      mercadoMensual += gasto;
    });

    // Market share por supermercado
    const marketShareData = {};
    Object.entries(IPSOS_SUPER.marketShare).forEach(([nombre, share]) => {
      marketShareData[nombre] = {
        share,
        clientes: Math.round(compranSuper * share),
        mercadoMensual: Math.round(mercadoMensual * share),
      };
    });

    return {
      compranSuper,
      noCompran,
      desgloseFrecuencia,
      consumosMensuales,
      consumosAnuales: consumosMensuales * 12,
      mercadoMensual,
      mercadoAnual: mercadoMensual * 12,
      marketShareData,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULOS TIENDAS DE CONVENIENCIA
  // =====================================================
  const resultadosConveniencia = useMemo(() => {
    if (!totalEjecutivos) return null;

    const usanConveniencia = Math.round(totalEjecutivos * IPSOS_CONVENIENCIA.penetracion);
    const noUsan = Math.round(totalEjecutivos * IPSOS_CONVENIENCIA.noPenetracion);

    // Desglose por frecuencia
    const desgloseFrecuencia = {};
    let consumosMensuales = 0;

    Object.entries(IPSOS_CONVENIENCIA.frecuenciaUso).forEach(([freq, data]) => {
      const personas = Math.round(usanConveniencia * data.pct);
      const consumos = Math.round(personas * data.factor);

      desgloseFrecuencia[freq] = {
        pct: data.pct,
        personas,
        consumos,
      };

      consumosMensuales += consumos;
    });

    const mercadoMensual = consumosMensuales * IPSOS_CONVENIENCIA.ticketPromedio;

    // Market share por tienda
    const marketShareData = {};
    Object.entries(IPSOS_CONVENIENCIA.marketShare).forEach(([nombre, share]) => {
      marketShareData[nombre] = {
        share,
        clientes: Math.round(usanConveniencia * share),
        mercadoMensual: Math.round(mercadoMensual * share),
      };
    });

    return {
      usanConveniencia,
      noUsan,
      desgloseFrecuencia,
      consumosMensuales,
      consumosAnuales: consumosMensuales * 12,
      mercadoMensual,
      mercadoAnual: mercadoMensual * 12,
      marketShareData,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN SUPERMERCADOS
  // =====================================================
  const calcularOportunidadSuper = useCallback(
    (supermercado, pctCaptacion) => {
      if (!resultadosSuper) return null;

      const datosSuper = resultadosSuper.marketShareData[supermercado];
      if (!datosSuper) return null;

      // Captar % del mercado total
      const mercadoCaptadoMensual = Math.round(resultadosSuper.mercadoMensual * (pctCaptacion / 100));

      return {
        mercadoTotal: resultadosSuper.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        pctDelTotal: pctCaptacion,
      };
    },
    [resultadosSuper]
  );

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN CONVENIENCIA
  // =====================================================
  const calcularOportunidadConveniencia = useCallback(
    (tienda, pctCaptacion) => {
      if (!resultadosConveniencia) return null;

      const datosTienda = resultadosConveniencia.marketShareData[tienda];
      if (!datosTienda) return null;

      // Captar % del mercado total
      const mercadoCaptadoMensual = Math.round(resultadosConveniencia.mercadoMensual * (pctCaptacion / 100));

      return {
        mercadoTotal: resultadosConveniencia.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        pctDelTotal: pctCaptacion,
      };
    },
    [resultadosConveniencia]
  );

  if (loading || edificios.length === 0) return <Loader />;

  const frecuenciaLabels = {
    diario: "Diariamente",
    variasSemana: "Varias veces por semana",
    semanal: "Una vez por semana",
    quincenal: "Cada dos semanas",
    mensual: "Una vez al mes o menos",
  };

  return (
    <div className={styles.retailPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>TIENDAS & CONVENIENCIAS</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.retailContent}>
        {/* Tabs para cambiar entre Supermercados y Conveniencia */}
        <div className={styles.tabsContainer}>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "super" && styles.tabActivo)}
            onClick={() => setVistaActiva("super")}
          >
            Supermercados
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "conveniencia" && styles.tabActivo)}
            onClick={() => setVistaActiva("conveniencia")}
          >
            Tiendas de Conveniencia
          </button>
        </div>

        {/* =====================================================
            VISTA SUPERMERCADOS
            ===================================================== */}
        {vistaActiva === "super" && resultadosSuper && (
          <>
            {/* Tabla de frecuencia */}
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thNoCompran)}>No compran</th>
                    <th className={cx("text-center", styles.thSiCompran)}>Sí compran</th>
                    <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de compra</th>
                    <th className={cx("text-center", styles.thPersonas)}>Personas</th>
                    <th className={cx("text-center", styles.thConsumo)}>Consumo prom.</th>
                    <th className={cx("text-center", styles.thConsumos)}>
                      <div className={styles.headerConsumos}>
                        <span>Consumos</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultadosSuper.desgloseFrecuencia).map(([freq, data], idx) => (
                    <tr key={freq}>
                      {idx === 0 && (
                        <>
                          <td rowSpan={5} className={cx("text-center align-middle", styles.celdaNo)}>
                            <div className={styles.celdaContenido}>
                              <span className={styles.badgeIpsos}>{(IPSOS_SUPER.noPenetracion * 100).toFixed(0)}%</span>
                              <div className={styles.valorGrande}>{formatNumber(resultadosSuper.noCompran)}</div>
                            </div>
                          </td>
                          <td rowSpan={5} className={cx("text-center align-middle", styles.celdaSi)}>
                            <div className={styles.celdaContenido}>
                              <span className={styles.badgeIpsos}>{(IPSOS_SUPER.penetracion * 100).toFixed(0)}%</span>
                              <div className={styles.valorGrande}>{formatNumber(resultadosSuper.compranSuper)}</div>
                            </div>
                          </td>
                        </>
                      )}
                      <td className={styles.celdaFrecuencia}>
                        <span className={styles.badgeIpsos}>{(data.pct * 100).toFixed(0)}%</span>
                        <span className={styles.frecuenciaTexto}>{frecuenciaLabels[freq]}</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">{formatLocal(convertCLP(data.ticket))}</td>
                      <td className="text-center">{formatNumber(verAnual ? data.consumos * 12 : data.consumos)}</td>
                    </tr>
                  ))}
                  <tr className={styles.filaTotales}>
                    <td colSpan={5} className={styles.celdaVacia} />
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>
                        {formatNumber(verAnual ? resultadosSuper.consumosAnuales : resultadosSuper.consumosMensuales)}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mercado total */}
            <div className={styles.mercadoTotalContainer}>
              <div className={styles.mercadoTotal}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}:</span>
                <span className={styles.mercadoValor}>
                  {formatLocal(convertCLP(verAnual ? resultadosSuper.mercadoAnual : resultadosSuper.mercadoMensual))}
                </span>
              </div>
            </div>

            {/* Market Share */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Market Share por Supermercado</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosSuper.marketShareData).map(([nombre, data]) => (
                  <div key={nombre} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{nombre}</div>
                    <div className={styles.cardShare}>
                      <span className={styles.badgeIpsos}>{(data.share * 100).toFixed(0)}%</span>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Clientes</span>
                        <span className={styles.metricaValor}>{formatNumber(data.clientes)}</span>
                      </div>
                      <div>
                        <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                        <span className={styles.metricaValor}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos / 338 usuarios de supermercados.
                Motivaciones de preferencia: cercanía al hogar/trabajo (48.4%), ofertas (39.5%), precios bajos (38%),
                variedad de productos (26.7%).
              </p>
            </div>
          </>
        )}

        {/* =====================================================
            VISTA TIENDAS DE CONVENIENCIA
            ===================================================== */}
        {vistaActiva === "conveniencia" && resultadosConveniencia && (
          <>
            {/* Tabla de frecuencia */}
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thNoCompran)}>No usan</th>
                    <th className={cx("text-center", styles.thSiCompran)}>Sí usan</th>
                    <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de uso</th>
                    <th className={cx("text-center", styles.thPersonas)}>Personas</th>
                    <th className={cx("text-center", styles.thConsumos)}>
                      <div className={styles.headerConsumos}>
                        <span>Visitas</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultadosConveniencia.desgloseFrecuencia).map(([freq, data], idx) => (
                    <tr key={freq}>
                      {idx === 0 && (
                        <>
                          <td rowSpan={5} className={cx("text-center align-middle", styles.celdaNo)}>
                            <div className={styles.celdaContenido}>
                              <span className={styles.badgeIpsos}>
                                {(IPSOS_CONVENIENCIA.noPenetracion * 100).toFixed(0)}%
                              </span>
                              <div className={styles.valorGrande}>{formatNumber(resultadosConveniencia.noUsan)}</div>
                            </div>
                          </td>
                          <td rowSpan={5} className={cx("text-center align-middle", styles.celdaSi)}>
                            <div className={styles.celdaContenido}>
                              <span className={styles.badgeIpsos}>
                                {(IPSOS_CONVENIENCIA.penetracion * 100).toFixed(0)}%
                              </span>
                              <div className={styles.valorGrande}>
                                {formatNumber(resultadosConveniencia.usanConveniencia)}
                              </div>
                            </div>
                          </td>
                        </>
                      )}
                      <td className={styles.celdaFrecuencia}>
                        <span className={styles.badgeIpsos}>{(data.pct * 100).toFixed(0)}%</span>
                        <span className={styles.frecuenciaTexto}>{frecuenciaLabels[freq]}</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">{formatNumber(verAnual ? data.consumos * 12 : data.consumos)}</td>
                    </tr>
                  ))}
                  <tr className={styles.filaTotales}>
                    <td colSpan={4} className={styles.celdaVacia} />
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>
                        {formatNumber(
                          verAnual ? resultadosConveniencia.consumosAnuales : resultadosConveniencia.consumosMensuales
                        )}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Ticket y Mercado */}
            <div className={styles.ticketMercadoContainer}>
              <div className={styles.ticketBox}>
                <span className={styles.ticketLabel}>Ticket promedio</span>
                <span className={styles.ticketValor}>{formatLocal(convertCLP(IPSOS_CONVENIENCIA.ticketPromedio))}</span>
              </div>
              <div className={styles.mercadoBox}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</span>
                <span className={styles.mercadoValor}>
                  {formatLocal(
                    convertCLP(verAnual ? resultadosConveniencia.mercadoAnual : resultadosConveniencia.mercadoMensual)
                  )}
                </span>
              </div>
            </div>

            {/* Market Share */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Market Share por Tienda</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosConveniencia.marketShareData).map(([nombre, data]) => (
                  <div key={nombre} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{nombre}</div>
                    <div className={styles.cardShare}>
                      <span className={styles.badgeIpsos}>{(data.share * 100).toFixed(0)}%</span>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Clientes</span>
                        <span className={styles.metricaValor}>{formatNumber(data.clientes)}</span>
                      </div>
                      <div>
                        <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                        <span className={styles.metricaValor}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos. Alta frecuencia de uso: 56.7% usa tiendas
                de conveniencia al menos varias veces por semana. Ticket promedio estimado: $8.000 CLP.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(
                    ({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork, created_at }) => (
                      <tr
                        key={created_at}
                        onClick={() => toggleCheck(id)}
                        className={cx(checkedBuildings[id] && "table-success")}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="text-center">
                          {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                        </td>
                        <td className="text-center">{edificio.toUpperCase()}</td>
                        <td className="text-center">{ubicacion}</td>
                        <td className="text-center">
                          {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                        </td>
                        <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (resultadosSuper || resultadosConveniencia) && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                Simulador de Oportunidad - {vistaActiva === "super" ? "Supermercados" : "Tiendas de Conveniencia"}
              </h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {vistaActiva === "super" && resultadosSuper && (
                <div className={styles.simuladorContainer}>
                  <div className={styles.simuladorControles}>
                    <div className={styles.controlGroup}>
                      <label>Supermercado objetivo:</label>
                      <select
                        value={supermercadoSeleccionado}
                        onChange={(e) => setSupermercadoSeleccionado(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_SUPER.marketShare).map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.controlGroup}>
                      <label>% captación mercado:</label>
                      <select
                        value={porcentajeCaptacion}
                        onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                        className="form-select form-select-sm"
                      >
                        {ESCENARIOS_CAPTACION.map((p) => (
                          <option key={p} value={p}>
                            {p}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const oportunidad = calcularOportunidadSuper(supermercadoSeleccionado, porcentajeCaptacion);
                    if (!oportunidad) return null;

                    return (
                      <div className={styles.simuladorResultados}>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Inversión referencia (3 meses):</span>
                          <span className={styles.resultadoValor}>{formatLocal(convertCLP(6000000))}</span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {vistaActiva === "conveniencia" && resultadosConveniencia && (
                <div className={styles.simuladorContainer}>
                  <div className={styles.simuladorControles}>
                    <div className={styles.controlGroup}>
                      <label>Tienda objetivo:</label>
                      <select
                        value={convenienciaSeleccionada}
                        onChange={(e) => setConvenienciaSeleccionada(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_CONVENIENCIA.marketShare).map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.controlGroup}>
                      <label>% captación mercado:</label>
                      <select
                        value={porcentajeCaptacion}
                        onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                        className="form-select form-select-sm"
                      >
                        {ESCENARIOS_CAPTACION.map((p) => (
                          <option key={p} value={p}>
                            {p}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const oportunidad = calcularOportunidadConveniencia(convenienciaSeleccionada, porcentajeCaptacion);
                    if (!oportunidad) return null;

                    return (
                      <div className={styles.simuladorResultados}>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Inversión referencia (3 meses):</span>
                          <span className={styles.resultadoValor}>{formatLocal(convertCLP(6000000))}</span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Retail;
